import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';

const labelStyle = {
  fontFamily: "'Barlow Condensed', sans-serif",
  color: 'rgba(147,197,253,0.7)',
  letterSpacing: '0.12em',
} as const;

const inputStyle = {
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(96,200,232,0.2)',
  padding: '11px 14px',
  color: 'white',
} as const;

const supportsPasskeys = typeof window !== 'undefined' && 'PublicKeyCredential' in window;

export function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signIn, signUp, signInWithPasskey } = useAuth();

  const [mode, setMode] = useState<'signIn' | 'register'>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Where the user was headed before being bounced here, if anywhere.
  const from = (location.state as { from?: string } | null)?.from;

  useEffect(() => {
    if (!loading && user) {
      navigate(from ?? '/options', { replace: true });
    }
  }, [loading, user, from, navigate]);

  const isRegistering = mode === 'register';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Please enter your email address and password.');
      return;
    }
    if (isRegistering && !displayName.trim()) {
      setError('Please enter your name.');
      return;
    }

    setSubmitting(true);
    try {
      if (isRegistering) {
        await signUp({ email: email.trim(), password, displayName: displayName.trim() });
      } else {
        await signIn({ email: email.trim(), password });
      }
      // The effect above redirects once the user lands in context.
    } catch (caught) {
      if (caught instanceof ApiError) {
        // Prefer a field message when validation rejected the password or email.
        const fieldMessage = caught.fieldErrors
          ? Object.values(caught.fieldErrors)[0]
          : undefined;
        setError(fieldMessage ?? caught.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function switchMode() {
    setMode(isRegistering ? 'signIn' : 'register');
    setError('');
    setPassword('');
  }

  async function handlePasskeySignIn() {
    setError('');
    setSubmitting(true);
    try {
      await signInWithPasskey();
      // The effect above redirects once the user lands in context.
    } catch (caught) {
      // NotAllowedError covers both an explicit cancel and a timeout — either way
      // there's nothing more specific worth telling the person than this.
      if (caught instanceof DOMException && caught.name === 'NotAllowedError') {
        setError('Passkey sign-in was cancelled.');
      } else if (caught instanceof ApiError) {
        setError(caught.message);
      } else {
        setError('Could not sign in with a passkey. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ fontFamily: "'Barlow', sans-serif" }}>

      {/* ── BACKGROUND PHOTO ── */}
      <div className="absolute inset-0 bg-slate-950">
        <img
          src="https://images.unsplash.com/photo-1687428896920-fae2683df1f3?w=1800&h=1200&fit=crop&auto=format"
          alt="Lake surrounded by rocky mountains under cloudy sky"
          className="w-full h-full object-cover"
          style={{ objectPosition: '50% 35%', opacity: 0.85 }}
        />
      </div>

      {/* ── GRADIENT LAYERS ── */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,6,12,0.78) 0%, rgba(2,6,12,0.35) 28%, transparent 48%)' }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(2,6,12,0.88) 0%, rgba(2,6,12,0.3) 30%, transparent 50%)' }} />

      {/* ── BRAND — same as options page, positioned in upper mountain zone ── */}
      <div
        className="absolute inset-x-0 top-0 flex flex-col items-center justify-center"
        style={{ height: '47%', paddingTop: '2vh' }}
      >
        <h1
          className="uppercase leading-none text-center"
          style={{
            fontFamily: "'Cinzel', serif",
            fontWeight: 600,
            fontSize: 'clamp(1.6rem, 5vw, 3.2rem)',
            color: '#ffffff',
            textShadow: '0 2px 48px rgba(0,0,0,0.9), 0 0 80px rgba(0,0,0,0.6)',
            letterSpacing: '0.06em',
            width: '100vw',
          }}
        >
          Black Rock
        </h1>
        <p
          className="uppercase mt-1 text-center"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(1.4rem, 3.5vw, 2.4rem)',
            letterSpacing: '0.24em',
            background: 'linear-gradient(90deg, #93c5fd 0%, #60c8e8 50%, #93c5fd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 16px rgba(77,217,247,0.5))',
          }}
        >
          Curling
        </p>
      </div>

      {/* ── SIGN IN FORM — anchored to bottom half ── */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-center" style={{ height: '72%' }}>
        <div
          className="w-full"
          style={{
            maxWidth: '360px',
            margin: '0 24px',
            background: 'rgba(4,12,24,0.7)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(96,200,232,0.22)',
            borderRadius: '16px',
            padding: '32px 28px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegistering && (
              <div>
                <label className="block text-xs uppercase tracking-wider mb-2" style={labelStyle}>
                  Your name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={e => { setDisplayName(e.target.value); setError(''); }}
                  className="w-full rounded-lg text-white text-sm focus:outline-none"
                  style={inputStyle}
                  placeholder="Enter your name"
                  autoComplete="name"
                />
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-wider mb-2" style={labelStyle}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                className="w-full rounded-lg text-white text-sm focus:outline-none"
                style={inputStyle}
                placeholder="Enter email address"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider mb-2" style={labelStyle}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                className="w-full rounded-lg text-white text-sm focus:outline-none"
                style={inputStyle}
                placeholder={isRegistering ? 'At least 8 characters' : 'Enter password'}
                autoComplete={isRegistering ? 'new-password' : 'current-password'}
              />
              {!isRegistering && (
                <Link
                  to="/forgot-password"
                  className="block text-right text-xs mt-2"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", color: 'rgba(147,197,253,0.55)', letterSpacing: '0.06em' }}
                >
                  Forgot password?
                </Link>
              )}
            </div>

            {error && <p className="text-red-400 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-lg uppercase tracking-wider transition-all duration-200 mt-2 disabled:opacity-60"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: '0.95rem',
                letterSpacing: '0.15em',
                background: 'linear-gradient(135deg, rgba(77,217,247,0.22) 0%, rgba(59,130,246,0.32) 100%)',
                border: '1px solid rgba(96,200,232,0.38)',
                color: '#e0f7ff',
                fontWeight: 700,
              }}
            >
              {submitting
                ? (isRegistering ? 'Creating account…' : 'Signing in…')
                : (isRegistering ? 'Create Account' : 'Sign In')}
            </button>

            {!isRegistering && supportsPasskeys && (
              <button
                type="button"
                onClick={handlePasskeySignIn}
                disabled={submitting}
                className="w-full py-2 rounded-lg uppercase tracking-wider transition-all duration-200 disabled:opacity-60"
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: '0.75rem',
                  letterSpacing: '0.15em',
                  background: 'transparent',
                  border: '1px solid rgba(96,200,232,0.2)',
                  color: 'rgba(147,197,253,0.6)',
                  fontWeight: 600,
                }}
              >
                Sign in with a passkey
              </button>
            )}

            <button
              type="button"
              onClick={switchMode}
              disabled={submitting}
              className="w-full py-2 rounded-lg uppercase tracking-wider transition-all duration-200 disabled:opacity-60"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: '0.75rem',
                letterSpacing: '0.15em',
                background: 'transparent',
                border: '1px solid rgba(96,200,232,0.2)',
                color: 'rgba(147,197,253,0.6)',
                fontWeight: 600,
              }}
            >
              {isRegistering ? 'Already registered? Sign in' : 'Register'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/options')}
              disabled={submitting}
              className="w-full py-2 rounded-lg uppercase tracking-wider transition-all duration-200 disabled:opacity-60"
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: '0.75rem',
                letterSpacing: '0.15em',
                background: 'transparent',
                border: '1px solid rgba(248,113,113,0.2)',
                color: 'rgba(252,165,165,0.7)',
                fontWeight: 600,
              }}
            >
              Guest Access — Watch Live Games
            </button>
          </form>
        </div>

        <p
          className="text-center text-xs mt-5"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", color: 'rgba(148,163,184,0.3)', letterSpacing: '0.05em' }}
        >
          © 2025 Black Rock Curling. All rights reserved.
        </p>
      </div>
    </div>
  );
}
